import React, { useEffect, useMemo, useState, useRef } from "react";
import api from "../api";
import { FaPlay, FaCheckCircle } from "react-icons/fa";

import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  useMapEvents,
  LayerGroup,
} from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import AsyncSiteSelect from "../AsyncSiteSelect";

/* ======================
   LEAFLET ICON FIX
====================== */
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

/* ======================
   COLORED MARKER
====================== */
const coloredMarker = (color) =>
  new L.Icon({
    iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-${color}.png`,
    shadowUrl:
      "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41],
  });

/* ======================
   MAP COMPONENT
====================== */
const LeafletMap = ({ alerts, concerns, onBoundsChange }) => {
  const allLocations = [
    ...alerts.map((a) => ({ ...a, type: "ALERT" })),
    ...concerns.map((c) => ({ ...c, type: "CONCERN" })),
  ];

  const MapEvents = () => {
    const map = useMapEvents({
      moveend: () => {
        onBoundsChange(map.getBounds());
      },
    });
    return null;
  };

  const getMarkerColor = (loc) => {
    if (loc.type === "ALERT") {
      if (loc.confidence >= 0.75) return "red";
      if (loc.confidence >= 0.5) return "orange";
      return "yellow";
    } else {
      switch (loc.status) {
        case "OPEN":
          return "red";
        case "IN_PROGRESS":
          return "orange";
        case "RESOLVED":
          return "green";
        default:
          return "blue";
      }
    }
  };

  return (
    <MapContainer
      style={{ height: "500px", width: "100%" }}
      center={[20.5937, 78.9629]}
      zoom={5}
      scrollWheelZoom
    >
      <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
      <MapEvents />
      <MarkerClusterGroup>
        {allLocations.map((loc, idx) => (
          <Marker
            key={idx}
            position={[loc.latitude || 20.5937, loc.longitude || 78.9629]}
            icon={coloredMarker(getMarkerColor(loc))}
          >
            <Popup>
              <div>
                {loc.type === "ALERT" ? (
                  <>
                    <b>Item Missing: {loc.item}</b>
                    <div>Emp: {loc.username || ""} / {loc.empId}</div>
                    <div>Site: {loc.siteName}</div>
                    <div>Confidence: {Math.round(loc.confidence * 100)}%</div>
                  </>
                ) : (
                  <>
                    <b>Concerns: {loc.filteredConcerns?.join(", ") || "-"}</b>
                    <div>Emp: {loc.username || ""} / {loc.empId}</div>
                    <div>Site: {loc.siteName}</div>
                    <div>Status: {loc.status}</div>
                  </>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
      </MarkerClusterGroup>

      {/* MAP LEGEND */}
      <LayerGroup>
        <div
          style={{
            position: "absolute",
            bottom: 10,
            left: 10,
            background: "#fff",
            padding: 8,
            borderRadius: 8,
            boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
            fontSize: 12,
          }}
        >
          <div>
            <span style={{ color: "red" }}>■</span> Open / High Confidence
          </div>
          <div>
            <span style={{ color: "orange" }}>■</span> In Progress / Medium
            Confidence
          </div>
          <div>
            <span style={{ color: "green" }}>■</span> Resolved / Low Concern
          </div>
        </div>
      </LayerGroup>
    </MapContainer>
  );
};

/* ======================
   SUMMARY CARD
====================== */
const SummaryCard = ({ title, value, color, icon, details, onClick }) => {
  const [show, setShow] = useState(false);

  return (
    <div
      style={{
        ...styles.summaryCard,
        borderLeft: `6px solid ${color}`,
        position: "relative",
      }}
      onClick={onClick}
      //onMouseEnter={() => window.innerWidth > 768 && setShow(true)}
      //onMouseLeave={() => window.innerWidth > 768 && setShow(false)}
    >
      <div style={{ fontSize: 24 }}>{icon}</div>
      <div>
        <div style={{ fontSize: 13, color: "#555" }}>{title}</div>
        <div style={{ fontSize: 22, fontWeight: 700 }}>{value}</div>
      </div>

      {/* tooltip remains same */}
      {show && details?.length > 0 && (
        <div style={styles.tooltip}>
          {details.map(d => (
            <div key={d.siteId}>
              {d.siteId}: {d.count}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


/* =====================
   TOAST
===================== */
const Toast = ({ message, type }) => {
  if (!message) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: 30,
        right: 30,
        padding: "12px 18px",
        borderRadius: 10,
        background: type === "success" ? "#00c853" : "#d50000",
        color: "#fff",
        fontWeight: 500,
        boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
        zIndex: 9999,
      }}
    >
      {message}
    </div>
  );
};

/* ======================
   MAIN COMPONENT
====================== */
export default function SupervisorDashboard() {
// 🔹 Working / filtered data
const [filteredAlerts, setFilteredAlerts] = useState([]);
const [filteredConcerns, setFilteredConcerns] = useState([]);

  // ORG-WIDE (never filtered)
const [globalAlerts, setGlobalAlerts] = useState([]);
const [globalConcerns, setGlobalConcerns] = useState([]);
const [globalEmployees, setGlobalEmployees] = useState(0);
const [globalActiveToday, setGlobalActiveToday] = useState(0);

const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showCompliance, setShowCompliance] = useState(true);
  const [showConcerns, setShowConcerns] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [view, setView] = useState("LIST");
const [selectedSites, setSelectedSites] = useState([]);
const [allSites, setAllSites] = useState([]);
const [expandCompliance, setExpandCompliance] = useState(true);
const [expandConcerns, setExpandConcerns] = useState(true);
const fetchTimeout = useRef(null);
const [employeeSummary, setEmployeeSummary] = useState({
  totalEmployees: 0,
  activeToday: 0,
});

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2500);
  };


const applyCardPrefilter = ({ type, siteIds }) => {
   const options = siteIds.map(id => ({
    value: id,
    label: siteNameMap[id] || id, // fallback safety
  }));
  console.log("options -", options);
  setSelectedSites(options);

  if (type === "ALERTS") {
    setShowCompliance(true);
    setShowConcerns(false);
    setFilters(f => ({ ...f, status: "" }));
    //setView("LIST");
  }

  if (type === "CONCERNS") {
    setShowCompliance(false);
    setShowConcerns(true);
    setFilters(f => ({ ...f, status: "OPEN" }));
    //setView("LIST");
  }

  if (type === "SITES") {
    setShowCompliance(true);
    setShowConcerns(true);
    //setView("LIST");
  }
};

const globalAlertSites = useMemo(
  () => [...new Set(globalAlerts.map(a => a.siteId))],
  [globalAlerts]
);

const globalConcernSites = useMemo(
  () => [...new Set(globalConcerns.map(c => c.siteId))],
  [globalConcerns]
);

const impactedSites = useMemo(
  () => [...new Set([...globalAlerts, ...globalConcernSites])],
  [globalAlertSites, globalConcernSites]
);

const siteNameMap = useMemo(() => {
  const map = {};

  globalAlerts.forEach(a => {
    if (a.siteId && a.siteName) {
      map[a.siteId] = a.siteName;
    }
  });
  
  globalConcerns.forEach(c => {
    if (c.siteId && c.siteName) {
      map[c.siteId] = c.siteName;
    }
  });

  return map;
}, [globalAlerts, globalConcerns]);

  const [filters, setFilters] = useState({
    status: "",
    empId: "",
    username: "",
    siteId: "",
    fromDate: "",
    toDate: "",
  });
const [pagination, setPagination] = useState({
  limit: 50,
  offset: 0,
});

const [mapBounds, setMapBounds] = useState(null);

const selectedSiteIds = selectedSites.map(s => s.value);

const selectedSiteAlerts = filteredAlerts.filter(a =>
  selectedSiteIds.includes(a.siteId)
);

const selectedSiteConcerns = filteredConcerns.filter(c =>
  selectedSiteIds.includes(c.siteId)
);

const selectedImpactedSites = new Set([
  ...selectedSiteAlerts.map(a => a.siteId),
  ...selectedSiteConcerns.map(c => c.siteId),
]).size;


useEffect(() => {

  const fetchGlobal = async () => {    
    const [alertsRes, concernsRes, res] = await Promise.all([
      api.get("/solution/supervisor/compliance-alerts/open"),
      api.get("/solution/supervisor/concerns/open"),
      api.get("/solution/supervisor/employees/summary"),
    ]);

    setGlobalAlerts(alertsRes.data);
    setGlobalConcerns(concernsRes.data);
    setGlobalEmployees(res.data.totalEmployees);
    setGlobalActiveToday(res.data.activeToday);

    setLoading(false);
  };

  fetchGlobal();
  const id = setInterval(fetchGlobal, 80000);
  return () => clearInterval(id);
}, []);


useEffect(() => {
    const fetchFiltered = async () => {

    console.log("selectedSites = ", selectedSites);
    const siteIdsParam = selectedSites.length
      ? selectedSites.map((s) => s.value).join(",")
      : undefined;

    const params = {
      ...filters,
      siteIds: siteIdsParam,   // ✅ IMPORTANT
      limit: pagination.limit,
      offset: pagination.offset,
    };

    console.log("selectedSites = ", params);

    const [alertsRes, concernsRes] = await Promise.all([
      api.get("/solution/supervisor/compliance-alerts/search", {params}),
      api.get("/solution/supervisor/concerns/search", {params}),
    ]);

    setFilteredAlerts(alertsRes.data || []);
    setFilteredConcerns(concernsRes.data || []);
  };

  // 🚨 key condition: do NOT fetch on every render
  if (
    selectedSites.length ||
    filters.status ||
    filters.empId ||
    filters.username ||
    filters.fromDate ||
    filters.toDate
  ) {
    console.log("fetching filtered", selectedSites);
    fetchFiltered();
  } else {
    console.log("fallback to global");
    // fallback = show global slice
    setFilteredAlerts(globalAlerts);
    setFilteredConcerns(globalConcerns);
  }
}, [selectedSites, filters]);

useEffect(() => {
  if (mapBounds) {
    fetchMapData(mapBounds);
  }
}, [filters.status, selectedSites]);

const fetchMapData = async (bounds) => {
  if (!bounds) return;

  const siteIdsParam = selectedSites.length
  ? selectedSites.map((s) => s.value).filter(Boolean).join(",")
  : undefined;
  
  const params = {
    north: bounds.getNorth(),
    south: bounds.getSouth(),
    east: bounds.getEast(),
    west: bounds.getWest(),
    status: filters.status || undefined,
    siteIds: siteIdsParam,   // ✅ SAME FILTERS
  };

  try {
    const res = await api.get("/solution/supervisor/map-data", { params });
    setFilteredAlerts(res.data.alerts || []);
    setFilteredConcerns(res.data.concerns || []);
  } catch (err) {
    console.error("Map fetch failed", err);
  }
};

const handleMapBoundsChange = (bounds) => {
   setMapBounds(bounds);

  if (fetchTimeout.current) {
    clearTimeout(fetchTimeout.current);
  }

  fetchTimeout.current = setTimeout(() => {
    fetchMapData(bounds);
  }, 300); // 250–400ms is ideal
};

  /* ========== ACTIONS ========== */
const updateConcernStatus = async (id, newStatus) => {
  let prevStatus;

  // 1️⃣ Optimistic UI update
  setFilteredConcerns(prev =>
    prev.map(c => {
      if (c._id === id) {
        prevStatus = c.status;   // capture old value
        return { ...c, status: newStatus };
      }
      return c;
    })
  );

  try {
    await api.patch(
      `${process.env.REACT_APP_URL}/solution/supervisor/concerns/${id}`,
      { status: newStatus }
    );

    showToast(`✅ Marked as ${newStatus.replace("_", " ")}`);
  } catch (err) {
    // 2️⃣ Proper rollback
    setFilteredConcerns(prev =>
      prev.map(c =>
        c._id === id ? { ...c, status: prevStatus } : c
      )
    );

    showToast("❌ Failed to update status", "error");
  }
};

const acknowledgeAlert = async (id) => {
  const newStatus = "ACKNOWLEDGED";
  let prevStatus;

  // 1️⃣ Optimistic UI update
  setFilteredAlerts(prev =>
    prev.map(a => {
      if (a._id === id) {
        prevStatus = a.status;
        return { ...a, status: newStatus };
      }
      return a;
    })
  );

  try {
    await api.patch(
      `${process.env.REACT_APP_URL}/solution/supervisor/compliance/${id}/ack`,
      { status: newStatus }
    );

    showToast("✅ Acknowledged");
  } catch (err) {
    // 2️⃣ Rollback
    setFilteredAlerts(prev =>
      prev.map(a =>
        a._id === id ? { ...a, status: prevStatus } : a
      )
    );

    showToast("❌ Failed to acknowledge", "error");
  }
};


  if (loading) return <div style={{ padding: 20 }}>Loading...</div>;

  /* ========== SUMMARY DETAILS PER SITE ========== */
  const alertSiteDetails = [...filteredAlerts.reduce((acc, a) => {
    acc.set(a.siteId, (acc.get(a.siteId) || 0) + 1);
    return acc;
  }, new Map())].map(([siteId, count]) => ({ siteId, count }));

  const concernSiteDetails = [...filteredConcerns.reduce((acc, c) => {
    acc.set(c.siteId, (acc.get(c.siteId) || 0) + 1);
    return acc;
  }, new Map())].map(([siteId, count]) => ({ siteId, count }));

  return (
    <div style={styles.page}>
      <h2>👮 Supervisor Dashboard</h2>

      {/* SUMMARY CARDS */}
        <div style={styles.summaryRow}>
        {/* UNIFORM ALERTS */}
        <SummaryCard
            title="Uniform Alerts"
            value={globalAlerts.length}
            color="#f44336"
            icon="🧥"
            details={alertSiteDetails}
            onClick={() =>
            applyCardPrefilter({
                type: "ALERTS",
                siteIds: globalAlertSites,
            })
            }
        />

        {/* OPEN CONCERNS */}
        <SummaryCard
            title="Open Concerns"
            value={globalConcerns.filter(c => c.status !== "RESOLVED").length}
            color="#ff9800"
            icon="📢"
            details={concernSiteDetails}
            onClick={() =>
            applyCardPrefilter({
                type: "CONCERNS",
                siteIds: globalConcernSites,
            })
            }
        />

        {/* IMPACTED SITES */}
        <SummaryCard
            title="Sites Impacted"
            value={impactedSites.length}
            color="#1976d2"
            icon="📍"
            onClick={() =>
            applyCardPrefilter({
                type: "SITES",
                siteIds: impactedSites,
            })
            }
        />


        <SummaryCard
        title="Total Employees"
        value={globalEmployees}
        icon="👥"
        color="#4caf50"
        onClick={() => {
            // Optional future drill-down
            //setView("LIST");
            setShowConcerns(false);
            setShowCompliance(false);
        }}
        />

        <SummaryCard
        title="Active Today"
        value={globalActiveToday}
        icon="🟢"
        color="#2e7d32"
        />

      </div>


      {/* FILTER PANEL */}
      {showFilters && (
       <div style={styles.filterPanelGrid}>
        <div>
        <AsyncSiteSelect
            value={selectedSites}
            onChange={setSelectedSites}
        />

        <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
        >
            <option value="">All Status</option>
            <option value="OPEN">OPEN</option>
            <option value="IN_PROGRESS">IN_PROGRESS</option>
            <option value="RESOLVED">RESOLVED</option>
        </select>
        </div>
       
        <div>
        <input
            type="date"
            value={filters.fromDate}
            onChange={(e) => setFilters({ ...filters, fromDate: e.target.value })}
        />

        <input
            type="date"
            value={filters.toDate}
            onChange={(e) => setFilters({ ...filters, toDate: e.target.value })}
        />
        </div>
         <div>
        <input
            placeholder="Emp ID"
            value={filters.empId}
            onChange={(e) => setFilters({ ...filters, empId: e.target.value })}
        />

        <input
            placeholder="Username"
            value={filters.username}
            onChange={(e) => setFilters({ ...filters, username: e.target.value })}
        />
        </div>

       <div style={styles.viewSwitch}>
        <button
            style={styles.resetBtn}
            onClick={() => {
            setFilters({
                status: "",
                empId: "",
                username: "",
                siteId: "",
                fromDate: "",
                toDate: "",
            });
            setSelectedSites([]);
            }}
        >
            Reset Filters
        </button>
        
        </div>
       </div>

      )}

        {/* SELECTED SITES SUMMARY */}
        {selectedSites.length > 0 && (
        <div style={{ ...styles.summaryRow, marginBottom: 12 }}>
         
            <SummaryCard
            title="Selected Sites Alerts"
            value={selectedSiteAlerts.length}
            color="#e53935"
            icon="🧥"
            />

            <SummaryCard
            title="Selected Sites Concerns"
            value={selectedSiteConcerns.filter(c => c.status !== "RESOLVED").length}
            color="#fb8c00"
            icon="📢"
            />

        
        </div>
        )}

      {/* TOGGLES */}
     <div style={styles.topBar}>
        <div style={styles.checkboxGroup}>
            <label style={styles.checkboxLabel}>
            <input
                type="checkbox"
                checked={showCompliance}
                onChange={() => setShowCompliance(v => !v)}
                style={styles.checkbox}
            />
            🔴 Compliance
            </label>

            <label style={styles.checkboxLabel}>
            <input
                type="checkbox"
                checked={showConcerns}
                onChange={() => setShowConcerns(v => !v)}
                style={styles.checkbox}
            />
            🟠 Concerns
            </label>
              
            {view === "LIST" ? (
            <label onClick={() => setView("MAP")}>📍 Map </label>
            ) : (
            <label onClick={() => setView("LIST")}>📄 List </label>
            )}
            
        </div>
    </div>
      {/* MAP VIEW */}
      {view === "MAP" && (
        <div style={styles.mapBox}>
          <LeafletMap
            alerts={filteredAlerts}
            concerns={filteredConcerns}
            onBoundsChange={handleMapBoundsChange}
            
          />
        </div>
      )}

      {/* LIST VIEW */}
      {view === "LIST" && (
        <>
        {showCompliance && (
        <section>
            <h3
            style={styles.sectionHeader}
            onClick={() => setExpandCompliance(v => !v)}
            >
            🔴 Uniform Compliance Alerts {expandCompliance ? "▲" : "▼"}
            </h3>

            {expandCompliance && (
            <>
                {filteredAlerts.length === 0 && <p>No alerts 🎉</p>}
                {filteredAlerts.map((a) => (
                <div key={a._id} style={styles.cardAlert}>
                    <div>
                    <b>{a.item?.toUpperCase()} Missing</b>
                    <div>Emp: {a.username || ""} / {a.empId}</div>
                    <div>Site: {a.siteName}</div>
                    <div>Confidence: {Math.round(a.confidence * 100)}%</div>
                    </div>
                    <div style={styles.actions}>
                    <button onClick={() => window.open(a.imageUrl)}>View</button>
                    <button onClick={() => acknowledgeAlert(a._id)}>Ack</button>
                    </div>
                </div>
                ))}
            </>
            )}
        </section>
        )}


          {showConcerns && (
            <section>
              <h3
                    style={styles.sectionHeader}
                    onClick={() => setExpandConcerns(v => !v)}
                    >
                    🟠 Employee Concerns {expandConcerns ? "▲" : "▼"}
               </h3>
              {expandConcerns && filteredConcerns.length === 0 && <p>No Concerns 🎉</p>}
              {expandConcerns && filteredConcerns.map((c) => {
                // Determine background color based on status
                let bgColor = "#ffecec"; // default light pink for OPEN
                if (c.status === "IN_PROGRESS") bgColor = "rgb(233 228 150)"; // light orange
                if (c.status === "RESOLVED") bgColor = "#e0ffe0"; // light green

                return (
                    <div key={c._id} style={{ ...styles.cardConcern, background: bgColor }}>
                    <div
                        style={{
                            position: "absolute",
                            marginTop: "-20px",
                            marginRight:"20px",
                            right: "12px",
                            padding: "4px 8px",
                            borderRadius: "12px",
                            fontSize: "14px",
                            fontWeight: 600,
                            background: bgColor,
                            color: "#0b0a0aff",
                        }}
                        >
                        {c.status}
                    </div>
                    
                    <div>
                        <b>{c.concerns?.join(", ") || "-"}</b>
                        <div><b>Emp: </b>{c.username || ""} / {c.empId}</div>
                        <div><b>Site: </b>{c.siteName}</div>
                        
                    </div>
                   <div style={styles.actions}>
                        {c.status !== "IN_PROGRESS" && (
                            <button
                            title="Mark In Progress"
                            onClick={() => updateConcernStatus(c._id, "IN_PROGRESS")}
                            style={{
                                ...styles.iconBtn,
                                background: "#ff9800",
                            }}
                            >
                            <FaPlay />
                            </button>
                        )}

                        {c.status !== "RESOLVED" && (
                            <button
                            title="Mark Resolved"
                            onClick={() => updateConcernStatus(c._id, "RESOLVED")}
                            style={{
                                ...styles.iconBtn,
                                background: "#4caf50",
                            }}
                            >
                            <FaCheckCircle />
                            </button>
                        )}
                    </div>

                </div>
                );
              })}

            </section>
          )}
        </>
      )}
    </div>
  );
}

/* ======================
   STYLES
====================== */
const styles = {
  page: { padding: 10 },
  summaryRow: { display: "flex", gap: 12, marginBottom: 16 },
  summaryCard: {
    flex: 1,
    background: "#fff",
    padding: 5,
    borderRadius: 12,
    display: "flex",
    gap: 12,
    alignItems: "center",
    boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
    cursor: "pointer",
  },
  toggleRow: { display: "flex", alignItems: "center", gap: 16, marginBottom: 12 },
  filterPanel: {
    background: "#fafafa",
    padding: 12,
    borderRadius: 10,
    marginBottom: 16,
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  cardAlert: {
    background: "#ffecec",
    padding: 12,
    borderRadius: 10,
    marginBottom: 12,
    display: "flex",
    justifyContent: "space-between",
  },
  iconBtn: {
    border: "none",
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
    marginRight: '35px',
    width: "50px",
  },

  cardConcern: {
    background: "#fff3e0",
    padding: 12,
    borderRadius: 10,
    marginBottom: 12,
    display: "flex",
    justifyContent: "space-between",
    
  },
  mapBox: { height: 500, borderRadius: 12, marginBottom: 20 },
  actions: { display: "flex", gap: 8 },
  topBar: {
  display: "flex",
  alignItems: "center",
  marginBottom: 12,
},

checkboxGroup: {
  display: "flex",
  gap: 14,
},

checkboxLabel: {
  display: "flex",
  alignItems: "center",
  gap: 6,
  fontSize: 13,
  cursor: "pointer",
},

checkbox: {
  width: 22,
  height: 22,
},
input: {
    margin:2,
},
select: {
margin:2,
},
viewSwitch: {
  marginLeft: "auto",
},

filterPanelGrid: {
  display: "grid",
 // gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gridTemplateColumns: "1fr 1fr 1fr",
  gap: 10,
  marginBottom: 16,
  background: "#fafafa",
  padding: 12,
  borderRadius: 12,
},

resetBtn: {
  gridColumn: "span 2",
},

sectionHeader: {
  cursor: "pointer",
  userSelect: "none",
},

};
